import { useCurrency } from '@/hooks/useCurrency'
import { DataTable, ReportPage, StatGrid, dateTimeLabel, dayLabel, useReport, exportCsv, type Column } from '@/components/reports/ReportKit'

interface DiscountBill {
  _id: string
  billNumber: string
  time: string | null
  table: string
  staff: string
  subTotal: number
  discount: number
  percent: number
  total: number
}
interface StaffRow {
  staff: string
  bills: number
  discount: number
}
interface DayRow {
  day: string
  discount: number
  roundOff: number
}
interface DiscountData {
  bills: DiscountBill[]
  byStaff: StaffRow[]
  byDay: DayRow[]
  summary: { totalDiscount: number; discountedBills: number; discountRate: number; roundOffNet: number; roundOffLoss: number }
}

export default function DiscountsScreen() {
  const { formatCurrency } = useCurrency()
  const report = useReport<DiscountData>('discounts')
  const data = report.data

  const billColumns: Column<DiscountBill>[] = [
    { key: 'billNumber', header: 'Bill', render: r => <span className="font-medium">#{r.billNumber}</span> },
    { key: 'time', header: 'Time', render: r => dateTimeLabel(r.time) },
    { key: 'table', header: 'Table' },
    { key: 'staff', header: 'Staff' },
    { key: 'subTotal', header: 'Sub-total', align: 'right', render: r => formatCurrency(r.subTotal) },
    { key: 'percent', header: 'Disc %', align: 'right', render: r => `${r.percent}%` },
    { key: 'discount', header: 'Discount', align: 'right', render: r => <span className="font-semibold text-red-500 dark:text-red-400">{formatCurrency(r.discount)}</span> },
    { key: 'total', header: 'Bill total', align: 'right', render: r => formatCurrency(r.total) }
  ]
  const staffColumns: Column<StaffRow>[] = [
    { key: 'staff', header: 'Staff', render: r => <span className="font-medium">{r.staff}</span> },
    { key: 'bills', header: 'Discounted bills', align: 'right' },
    { key: 'discount', header: 'Total discount', align: 'right', render: r => formatCurrency(r.discount) }
  ]
  const dayColumns: Column<DayRow>[] = [
    { key: 'day', header: 'Day', render: r => dayLabel(r.day) },
    { key: 'discount', header: 'Discount', align: 'right', render: r => formatCurrency(r.discount) },
    { key: 'roundOff', header: 'Round-off (net)', align: 'right', render: r => formatCurrency(r.roundOff) }
  ]

  return (
    <ReportPage
      eyebrow="Money · Leakage"
      title="Discounts & Round-off"
      subtitle="Which bills were discounted, by whom, and what round-off cost over time"
      report={report}
      onExport={() => exportCsv('discounts', billColumns, data?.bills ?? [])}
    >
      {data && (
        <>
          <StatGrid
            stats={[
              { label: 'Total discount given', value: formatCurrency(data.summary.totalDiscount), tone: data.summary.totalDiscount > 0 ? 'bad' : undefined, sub: `${data.summary.discountRate}% of discounted bills` },
              { label: 'Discounted bills', value: data.summary.discountedBills },
              { label: 'Round-off (net)', value: formatCurrency(data.summary.roundOffNet), tone: data.summary.roundOffNet < 0 ? 'bad' : undefined },
              { label: 'Round-off lost', value: formatCurrency(data.summary.roundOffLoss), sub: 'Rounded down on bills' }
            ]}
          />
          <DataTable title="By staff" columns={staffColumns} rows={data.byStaff} rowKey={r => r.staff} defaultSort="discount" empty="No discounts given." />
          <DataTable title="Discounted bills" columns={billColumns} rows={data.bills} rowKey={r => r._id} defaultSort="discount" csvName="discounted-bills" empty="No discounts given in this period." />
          <DataTable title="Day by day" columns={dayColumns} rows={data.byDay} rowKey={r => r.day} defaultSort="day" empty="No data." />
        </>
      )}
    </ReportPage>
  )
}
